import { groupBy, isEmpty, startCase } from 'lodash';
import { LIST_WIDTH } from 'utils/constants/listview';
import { TASK_MODEL } from '../../../services/entity/task';
import { DEFAULT_EMPTY_VALUE, PAGE_NAME } from '../../constants';
import { TASK_EVIDENCE_TYPE, TASK_TYPE } from '../../constants/task';
import { getItemOrDefaultToValue, nestedDisplay } from '../../details';
import { getStatusValue, getUserStringForList } from '../../helper/task';
import { displayTime, formatTimeDifference } from '../../time';
import { SCHEDULE_PATTERNS } from 'utils/constants/schedule';
import { GEOGRAPHICAL_MAP } from 'utils/constants/Map';
import find from 'lodash/find';
import get from 'lodash/get';
import { serviceTypes } from 'utils/constants/serviceTypes';
import { converSeverityRatingToText } from 'containers/Pages/Events/Layouts';
import { formatContents, fromJSON } from 'utils/helper/baseTasks';

export const convertPercentageToimageCordinates = (taskDetail, surfaces) => {
  return taskDetail.map(m => {
    // get the surfaceimage object if the id matches
    const surfaceImage = surfaces.find(f => f.id === m.surfaceId);

    if (surfaceImage) {
      const u = parseFloat((m.surfaceU * surfaceImage.width).toFixed(2));
      const v = parseFloat(
        (surfaceImage.height - m.surfaceV * surfaceImage.height).toFixed(2),
      );
      return {
        ...m,
        surfaceV: m.surfaceV ? u : null,
        surfaceU: m.surfaceU ? v : null,
      };
    } else return m;
  });
};

export const transformSubTask = ({ data, tasks }) => {
  return data.map(item => {
    const value = tasks.find(task => task.id === item.subtaskId);
    return {
      [TASK_MODEL.id.id]: value.id,
      [TASK_MODEL.taskName.id]: getItemOrDefaultToValue(
        value,
        TASK_MODEL.taskName.id,
        DEFAULT_EMPTY_VALUE,
      ),
      [TASK_MODEL.description.id]: getItemOrDefaultToValue(
        value,
        TASK_MODEL.description.id,
        DEFAULT_EMPTY_VALUE,
      ),
    };
  });
};

export const columns = [
  {
    field: TASK_MODEL.taskName.id,
    headerName: TASK_MODEL.taskName.label,
    width: LIST_WIDTH.sm,
  },
  {
    headerName: TASK_MODEL.description.label,
    field: TASK_MODEL.description.id,
    width: LIST_WIDTH.lg,
  },
];

export const evidenceColumns = [
  {
    field: TASK_MODEL.evidence.type.id,
    headerName: TASK_MODEL.evidence.type.label,
    width: LIST_WIDTH.sm,
  },
  {
    headerName: TASK_MODEL.evidence.description.label,
    field: TASK_MODEL.evidence.description.id,
    width: LIST_WIDTH.lg,
  },
];
export const collectedEvidenceColumns = [
  {
    headerName: 'Status',
    field: TASK_MODEL.evidence.completed.id,
    cellRenderer: 'statusComponent',
    width: LIST_WIDTH.sm,
  },
  {
    field: TASK_MODEL.evidence.type.id,
    headerName: TASK_MODEL.evidence.type.label,
    width: LIST_WIDTH.sm,
  },
  {
    headerName: TASK_MODEL.evidence.description.label,
    field: TASK_MODEL.evidence.description.id,
    width: LIST_WIDTH.lg,
  },
  {
    headerName: 'Contents',
    field: TASK_MODEL.evidence.contents.id,
    width: LIST_WIDTH.lg,
    cellRenderer: 'audioComponent',
  },
  {
    headerName: 'Time Taken',
    field: TASK_MODEL.evidence.timestamp.id,
    width: LIST_WIDTH.md,
  },
];

export const scheduleColumns = [
  {
    field: TASK_MODEL.schedulePatterns.scheduleType.id,
    headerName: TASK_MODEL.schedulePatterns.scheduleType.label,
    width: LIST_WIDTH.sm,
  },
  {
    headerName: TASK_MODEL.businessClient.name.label,
    field: TASK_MODEL.businessClient.name.field,
    width: LIST_WIDTH.lg,
  },
  {
    headerName: TASK_MODEL.site.name.label,
    field: TASK_MODEL.site.name.field,
    width: LIST_WIDTH.lg,
  },
];

export const transformEvidence = ({ data = [], timeZone }) => {
  const sorted = [...data]
    .map(item => ({
      description: item[TASK_MODEL.evidence.description.id],
      type: item[TASK_MODEL.evidence.type.id],
      contents: item[TASK_MODEL.evidence.contents.id],
      completed: item[TASK_MODEL.evidence.completed.id],
      timestamp: item[TASK_MODEL.evidence.timestamp.id]
        ? displayTime({
            timestamp: item[TASK_MODEL.evidence.timestamp.id],
            timeZone,
          })
        : undefined,
    }))
    .sort((a, b) => {
      const displayLast = [
        TASK_EVIDENCE_TYPE.IMAGE.id,
        TASK_EVIDENCE_TYPE.AUDIO.id,
        TASK_EVIDENCE_TYPE.VIDEO.id,
      ];
      const hasImageA = displayLast.includes(a.type);
      const hasImageB = displayLast.includes(b.type);
      if (hasImageA && hasImageB) {
        return b.completed ? 1 : 0;
      }
      if (hasImageB) {
        return -1;
      }

      return b.completed ? 0 : -1;
    });
  return groupBy(sorted, 'type');
};

export const transformRawDetails = ({
  tasks,
  dummyTaskData,
  globalDepartmentList = [],
  siteList = [],
  users,
}) => {
  const transformedTasks = [...tasks.tasks];
  // If a task is not a dummy task, then add assigned departments to sites and tasks
  if (!dummyTaskData.isDummyTask) {
    //get the root task
    const rootTask = transformedTasks.find(i => i.id === dummyTaskData.id);
    const rootTaskIndX = transformedTasks.findIndex(
      i => i.id === dummyTaskData.id,
    );
    // selected task assigned departments
    const selectedDepartments = globalDepartmentList.filter(f =>
      getItemOrDefaultToValue(rootTask, 'departmentIds', []).includes(f.id),
    );
    // This will give the assigned departments for the linked site
    const assignedDepartmentsSites =
      siteList.find(f => f.id === rootTask?.site.id)?.assignedDepartmentList ||
      [];

    const siteWithAssignedDepartment = {
      id: get(rootTask, 'site.id', ''),
      name: get(rootTask, 'site.name', ''),
      assignedDepartmentList: rootTask?.site?.id
        ? assignedDepartmentsSites
        : [],
    };
    // add departments and assigned department for linked sites

    transformedTasks[rootTaskIndX] = {
      ...transformedTasks[rootTaskIndX],
      departments: selectedDepartments,
      site: {
        ...transformedTasks[rootTaskIndX].site,
        ...siteWithAssignedDepartment,
      },
      assigneeFullName: rootTask.assigneeId
        ? users[rootTask[TASK_MODEL.assignee.id]] || DEFAULT_EMPTY_VALUE
        : rootTask.assigneeFullName,
    };
  }

  if (isEmpty(dummyTaskData) || !dummyTaskData.isDummyTask) {
    tasks['tasks'] = transformedTasks;
    return tasks;
  }

  const rootTaskIndex = transformedTasks.findIndex(
    i => i.id === dummyTaskData.templateId,
  );

  // const { schedulePattern } = dummyTaskData;

  transformedTasks[rootTaskIndex] = {
    ...transformedTasks[rootTaskIndex],
    site: dummyTaskData.site,
    businessClient: dummyTaskData.businessClient,
    // FIX-ME: SET surfaceId
    // ...(schedulePattern.surfaceU && {
    //   surfaceU: schedulePattern.surfaceU,
    //   surfaceV: schedulePattern.surfaceV,
    // }),
    siteId: dummyTaskData.site.id,
    businessClientId: dummyTaskData.businessClient.id,
  };
  tasks['tasks'] = transformedTasks;
  return tasks;
};

export const transformDetails = ({
  id,
  tasks,
  joins,
  location,
  users,
  dummyTaskData, // this is selected data when a task is selected
  sites,
  businessClients,
  surfaces,
  siteList,
  centerOfGravity,
  globalDepartmentList,
  pageName,
}) => {
  const taskData = tasks
    ? tasks
    : dummyTaskData &&
      dummyTaskData.isDummyTask &&
      !isEmpty(dummyTaskData).schedulePattern &&
      tasks.map(item => {
        const { siteId, businessClientId } = dummyTaskData.schedulePattern;
        return {
          ...item,
          siteId,
          businessClientId,
          businessClient: dummyTaskData.businessClient,
          site: dummyTaskData.site,
          surfaceU: dummyTaskData.surfaceU,
          surfaceV: dummyTaskData.surfaceV,
          pageName,
        };
      });

  const taskList = transformTaskDetail({
    id,
    tasks: taskData,
    dummyTaskData,
    joins,
    surfaces,
    users,
    sites,
    businessClients,
    location,
    siteList: siteList,
    centerOfGravity,
    globalDepartmentList,
    pageName,
  });
  switch (location) {
    case PAGE_NAME.MANAGERIAL:
      return transformViewDetail(taskList);
    default:
      return taskList;
  }
};

export const transformViewDetail = data => {
  if (Object.keys(data).length > 0) {
    const {
      finishedBy,
      startedBy,
      status,
      timeStarted,
      timeEnded,
      assignee,
      elapsedTime,
      ...rest
    } = data.generalInfo;
    /*eslint no-unused-vars: ["error", { "ignoreRestSiblings": true }]*/
    return { ...data, generalInfo: rest };
  }
  return data;
};

export const transformScheduleType = data => {
  const scheduleType = Object.values(SCHEDULE_PATTERNS).find(
    item => item.id === data,
  );
  return isEmpty(scheduleType) ? data : startCase(scheduleType.label);
};

const taskIconSelector = taskType => {
  switch (taskType) {
    case serviceTypes.maintenance:
      return TASK_TYPE.MAINTENANCE.mapIcon;
    case serviceTypes.cleaning:
      return TASK_TYPE.CLEANING.mapIcon;
    case serviceTypes.security:
      return TASK_TYPE.SECURITY.mapIcon;
    default:
      return '-';
  }
};

const getImageOrMapMarkers = (data, taskDetail, centerOfGravity) => {
  const { lat, lng } = centerOfGravity;

  if (!data.surfaceId && centerOfGravity) {
    return {
      addressLocationMarker: {
        // TO:DO  change the shap to match {cleaningTask :{items: [{}]}}
        icon: taskIconSelector(data[TASK_MODEL.taskCategory.id]),
        lat: data.surfaceV,
        lng: data.surfaceU,
      },

      center: {
        lat: data.surfaceV ? data.surfaceV : lat,
        lng: data.surfaceU ? data.surfaceU : lng,
      },
      id: data.site.id,
      tableView: false,
      boundary: taskDetail ? taskDetail.locationDetail[0].paths : [],
    };
  } else {
    return [
      {
        icon: taskIconSelector(data[TASK_MODEL.taskCategory.id]),
        lat: data.surfaceV,
        lng: data.surfaceU,
      },
    ];
  }
};
export const transformTaskDetail = ({
  id,
  tasks,
  joins,
  timeZone,
  currentTime,
  users,
  // sites,
  businessClients,
  location,
  surfaces,
  siteList,
  centerOfGravity,
  dummyTaskData,
  globalDepartmentList,
  pageName,
}) => {
  let data = tasks.find(item => item.id === id);
  // replace the schedule patterns of the parent tasks with that of the dummyTaskData task
  if (
    data &&
    Object.prototype.hasOwnProperty.call(data, 'schedulePatterns') &&
    dummyTaskData?.isDummyTask
  ) {
    Reflect.deleteProperty(data, 'schedulePatterns');

    data = {
      ...data,
      schedulePatterns: [dummyTaskData.schedulePattern],
    };
  }
  if (!data) {
    return {};
  }

  const haveSubTask = Array.isArray(joins) && joins.length > 0;
  const schedule = data[TASK_MODEL.schedule.id];
  const schedulePatterns = data[TASK_MODEL.schedulePatterns.id];
  const evidence = data[TASK_MODEL.evidence.id];

  const taskDetail = find(siteList, { id: get(data, 'siteId') });
  // This gives the list of assigned departments for this customer
  const selectedDepartments = globalDepartmentList.filter(f =>
    getItemOrDefaultToValue(data, 'departmentIds', []).includes(f.id),
  );
  // This will give the assigned departments for the linked site
  const assignedDepartmentsSites =
    siteList.find(f => f.id === data?.site.id)?.assignedDepartmentList || [];

  const siteWithAssignedDepartment = {
    id: get(data, 'site.id', ''),
    name: get(data, 'site.name', ''),
    assignedDepartmentList: data?.site?.id ? assignedDepartmentsSites : [],
  };
  const evidenceContent = item => {
    if (
      item.completed &&
      item.contents &&
      (item.type === TASK_EVIDENCE_TYPE.OPTION_SINGLE.id ||
        item.type === TASK_EVIDENCE_TYPE.OPTION_MULTIPLE.id)
    ) {
      return formatContents(item.contents);
    } else if (item.completed && item.contents) {
      //for other type if evidence like image, audio, text etc
      return item.contents;
    }
    // if not completed then '-'
    return DEFAULT_EMPTY_VALUE;
  };

  const contentType = ({ type, contents }) => {
    if (
      type === TASK_EVIDENCE_TYPE.OPTION_SINGLE.id ||
      type === TASK_EVIDENCE_TYPE.OPTION_MULTIPLE.id
    ) {
      return fromJSON(contents);
    }

    return contents;
  };
  const transformCollectedEvidences = ({ evidence }) => {
    return (evidence || [])
      .filter(f => f.completed)
      .map(
        // only curate the completed evidences
        ({ type, description, id, contents, completed, timestamp } = {}) => {
          return {
            type,
            description: description,
            id,
            completed: { value: completed },
            contents: {
              type,
              value: contentType({ type, contents }),
            },
            timestamp,
            textOptions: {
              display: undefined,
              displayValue: '',
              doNotShow: true,
              value: null,
              type: 'dynamic-text-generator',
            },
          };
        },
      );
  };
  return {
    generalInfo: {
      taskName: {
        type: 'text',
        value: getItemOrDefaultToValue(data, TASK_MODEL.taskName.id, '-'),
      },

      description: {
        type: 'text',
        value: data[TASK_MODEL.description.id] || DEFAULT_EMPTY_VALUE,
      },

      taskType: {
        type: 'text',
        value: data[TASK_MODEL.taskType.id] || DEFAULT_EMPTY_VALUE,
      },
      taskCategory: {
        type: 'icons',
        value: data['categoryIcon'] || DEFAULT_EMPTY_VALUE,
      },
      severity: {
        type: 'text',
        value: data?.severity || DEFAULT_EMPTY_VALUE,
      },
      severityRating: {
        type: 'text',
        value:
          converSeverityRatingToText(data?.severityRating) ||
          DEFAULT_EMPTY_VALUE,
      },
      businessClientId: {
        type: 'text',
        label: 'Business Client ID',
        value: data.businessClient
          ? getItemOrDefaultToValue(
              data.businessClient,
              TASK_MODEL.businessClient.businessClientId.id,
              DEFAULT_EMPTY_VALUE,
            )
          : DEFAULT_EMPTY_VALUE,
      },

      businessClientName: {
        type: 'text',
        value: getItemOrDefaultToValue(
          data.businessClient,
          TASK_MODEL.businessClient.name.id,
          DEFAULT_EMPTY_VALUE,
        ),
      },

      siteId: {
        type: 'text',
        label: 'Site ID',
        value: data.site
          ? getItemOrDefaultToValue(
              data.site,
              TASK_MODEL.site.siteId.id,
              DEFAULT_EMPTY_VALUE,
            )
          : DEFAULT_EMPTY_VALUE,
      },

      siteName: {
        type: 'text',
        value: data.site
          ? getItemOrDefaultToValue(
              data.site,
              TASK_MODEL.site.name.id,
              DEFAULT_EMPTY_VALUE,
            )
          : DEFAULT_EMPTY_VALUE,
      },

      startedBy: {
        type: 'text',
        value: getItemOrDefaultToValue(
          data,
          TASK_MODEL.startedByName.id,
          DEFAULT_EMPTY_VALUE,
        ),
      },

      finishedBy: {
        type: 'text',
        value: getItemOrDefaultToValue(
          data,
          TASK_MODEL.finishedByName.id,
          DEFAULT_EMPTY_VALUE,
        ),
      },

      timeStarted: {
        type: 'text',
        value: data[TASK_MODEL.timeStarted.id]
          ? displayTime({
              timestamp: getItemOrDefaultToValue(
                data,
                TASK_MODEL.timeStarted.id,
              ),
            })
          : DEFAULT_EMPTY_VALUE,
      },

      timeEnded: {
        type: 'text',
        value: data[TASK_MODEL.timeEnded.id]
          ? displayTime({
              timestamp: getItemOrDefaultToValue(
                data,
                TASK_MODEL.timeEnded.id,
                '-',
              ),
            })
          : DEFAULT_EMPTY_VALUE,
      },

      elapsedTime: {
        type: 'text',
        value: data[TASK_MODEL.timeStarted.id]
          ? formatTimeDifference(
              data[TASK_MODEL.timeStarted.id],
              data[TASK_MODEL.timeEnded.id] || currentTime,
            )
          : '0s',
      },

      assignee: {
        type: 'text',
        value: data.assigneeId
          ? users[data[TASK_MODEL.assignee.id]] || DEFAULT_EMPTY_VALUE
          : data.schedulePatterns
          ? getUserStringForList(
              users,
              data.schedulePatterns[0].assignToUserIDs,
            )
          : DEFAULT_EMPTY_VALUE,
        idValue: data.assigneeId,
      },
      status: {
        type: 'text',
        idValue: data[TASK_MODEL.status.id],
        value: data.status
          ? getStatusValue(data, TASK_MODEL.status.id)
          : DEFAULT_EMPTY_VALUE,
      },
      asset: {
        type: 'text',
        value: getItemOrDefaultToValue(data.asset, 'name'),
      },
      departments: {
        type: 'array',
        label: 'Departments',
        value: nestedDisplay(selectedDepartments),
      },
      archived: {
        type: 'boolean',
        value: getItemOrDefaultToValue(data, 'archived', ''),
      },
    },
    detailedInfo: {
      ...(evidence && {
        evidence: {
          type: 'list-data',
          value: {
            data: evidence
              .filter(f => f.type !== TASK_EVIDENCE_TYPE.CHECKBOX.id)
              .map(item => ({
                type: TASK_EVIDENCE_TYPE[item.type].name,
                completed: item.completed,
                description: item.description,
                contents: evidenceContent(item),
                timestamp:
                  item.timestamp && item.completed
                    ? displayTime({ timestamp: item.timestamp })
                    : DEFAULT_EMPTY_VALUE,
              })),
            columns: collectedEvidenceColumns,
          },
        },
        checkList: {
          type: 'list-data',
          value: {
            data: evidence
              .filter(f => f.type === TASK_EVIDENCE_TYPE.CHECKBOX.id)
              .map(item => ({
                type: TASK_EVIDENCE_TYPE[item.type].name,
                completed: item.completed,
                description: item.description,
                contents: item.completed ? item.contents : DEFAULT_EMPTY_VALUE, //make - if status false
                timestamp:
                  item.timestamp && item.completed
                    ? displayTime({ timestamp: item.timestamp })
                    : DEFAULT_EMPTY_VALUE,
              })),
            //depends on if it is on Managerial or Not.
            columns:
              location === PAGE_NAME.MANAGERIAL
                ? evidenceColumns
                : collectedEvidenceColumns,
          },
        },
      }),
      ...(schedulePatterns && {
        schedule: {
          type: 'list-data',
          value: {
            data: schedulePatterns.map(item => ({
              scheduleType: transformScheduleType(item.scheduleType),
              businessClientName: item.businessClientId
                ? businessClients[item.businessClientId]
                : item.businessClientId,
              siteName: item.siteId
                ? siteList.find(f => f.id === item.siteId)?.name
                : item.siteId,
            })),
            columns: scheduleColumns,
          },
        },
      }),
      ...(schedule && {
        schedule: {
          type: 'schedule',
          value: {
            startAfter: {
              type: 'text',
              value: schedule[TASK_MODEL.schedule.startAfter.id]
                ? displayTime({
                    timestamp: schedule[TASK_MODEL.schedule.startAfter.id],
                  })
                : DEFAULT_EMPTY_VALUE,
            },
            finishBefore: {
              type: 'text',
              value: schedule[TASK_MODEL.schedule.finishBefore.id]
                ? displayTime({
                    timestamp: schedule[TASK_MODEL.schedule.finishBefore.id],
                  })
                : DEFAULT_EMPTY_VALUE,
            },
          },
        },
      }),

      ...(haveSubTask && {
        subtasks: {
          type: 'list-data',
          value: {
            data: transformSubTask({
              data: joins,
              tasks: tasks,
              timeZone,
            }),
            columns,
          },
        },
      }),
      site: {
        type: data.surfaceId !== null ? 'image-marker' : 'map-array',
        value: getImageOrMapMarkers(data, taskDetail, centerOfGravity),
        ...(data.surfaceId !== null &&
          surfaces && {
            // add imageUrl as a field incase the surfaceId and surfaces are set
            imageUrl: surfaces.filter(f => f.id === data.surfaceId)[0][
              'imageUrl'
            ],
          }),
      },
    },
    others: {
      pageName: {
        value: pageName,
      },
      evidenceCollection: {
        // collect all the completed evidences for evidence-collection component
        value: transformCollectedEvidences({ evidence }),
      },
      image: {
        value: getItemOrDefaultToValue(data, TASK_MODEL.descriptionImageUrl.id),
      },
      id: { value: data[TASK_MODEL.id.id] },
      surfaceU: {
        value: data[TASK_MODEL.surfaceU.id],
      },
      surfaceV: { value: data[TASK_MODEL.surfaceV.id] },
      expectedDuration: {
        value: data[TASK_MODEL.expectedDuration.id],
      },
      site: {
        value: siteWithAssignedDepartment,
      },
      isTemplate: { value: data.isTemplate },
      surfaces,
      siteSurfaceImages:
        data.surfaceId === null
          ? GEOGRAPHICAL_MAP
          : surfaces.filter(f => f.id === data.surfaceId),
    },
  };
};
